import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  collection, doc, getDoc, onSnapshot, orderBy, query,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { applyPaywallCredit, createStripeCheckout, markRead, sendMessage, submitPaywallIntent, unmatch } from "../lib/api";
import { friendly } from "../lib/errors";
import { TIER_LABEL, TIER_PRICE, TIER_SIZE, type DeckCard, type MatchDoc, type MessageDoc, type Tier } from "../lib/types";
import Sheet from "../components/Sheet";
import ReportSheet from "../components/ReportSheet";
import ProfileDetail from "../components/ProfileDetail";
import Countdown from "../components/Countdown";
import { useVisualViewport } from "../lib/useVisualViewport";

export default function Chat() {
  const { matchId } = useParams<{ matchId: string }>();
  const { uid } = useAuth();
  const navigate = useNavigate();

  const [match, setMatch] = useState<MatchDoc | null>(null);
  const [gone, setGone] = useState(false); // deleted / expired / not yours
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [other, setOther] = useState<{ name: string; photo: string | null; prompts: { prompt: string; answer: string }[] }>({ name: "…", photo: null, prompts: [] });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [peerProfile, setPeerProfile] = useState<DeckCard | null>(null);
  const [pending, setPending] = useState<string[]>([]); // optimistic sends
  const vv = useVisualViewport();
  const [confirmUnmatch, setConfirmUnmatch] = useState(false);
  const [unmatching, setUnmatching] = useState(false);
  const [paywallBusy, setPaywallBusy] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Match doc listener.
  useEffect(() => {
    if (!matchId || !uid) return;
    return onSnapshot(
      doc(db, "matches", matchId),
      (snap) => {
        if (!snap.exists()) { setGone(true); return; }
        const m = snap.data() as MatchDoc;
        if (!m.participants.includes(uid) || m.state === "expired") { setGone(true); return; }
        setMatch(m);
      },
      () => setGone(true), // permission denied ⇒ not ours / removed
    );
  }, [matchId, uid]);

  // Messages listener.
  useEffect(() => {
    if (!matchId || !uid || gone) return;
    const q = query(collection(db, "matches", matchId, "messages"), orderBy("sentAt", "asc"));
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MessageDoc, "id">) }));
      setMessages(list);
      // Drop optimistic copies once the real message lands.
      setPending((p) => p.filter((t) => !list.some((m) => m.senderId === uid && m.text === t)));
    });
  }, [matchId, uid, gone]);

  // Other participant's profile.
  const otherUid = useMemo(
    () => match?.participants.find((p) => p !== uid) ?? null,
    [match, uid],
  );
  useEffect(() => {
    if (!otherUid) return;
    void getDoc(doc(db, "users", otherUid))
      .then((s) => setOther({
        name: (s.data()?.displayName as string) ?? "Someone",
        photo: (s.data()?.photoURLs as string[] | undefined)?.[0] ?? null,
        prompts: (s.data()?.prompts as { prompt: string; answer: string }[]) ?? [],
      }))
      .catch(() => setOther({ name: "Someone", photo: null, prompts: [] }));
  }, [otherUid]);

  // "Break the ice" starters (iOS ChatView): up to 2 built from their prompt
  // answers, then the generic openers.
  const starters = useMemo(() => {
    const first = other.name.split(" ")[0];
    const personalized = other.prompts
      .filter((p) => p.answer.trim().length >= 12) // one-word answers make weird starters
      .slice(0, 2)
      .map((p) => `"${p.answer.length > 48 ? p.answer.slice(0, 48) + "…" : p.answer}" — tell me more 👀`);
    return [
      ...personalized,
      `Hey ${first}! What's been the best part of your week?`,
      "What are you into lately — music, food, or trouble?",
      "Two truths and a lie. Go 😏",
      "Perfect day, nothing off-limits — what's it look like?",
    ];
  }, [other]);

  // Mark read when messages arrive while open.
  useEffect(() => {
    if (!matchId || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.senderId !== uid) void markRead({ matchId }).catch(() => {});
  }, [matchId, messages, uid]);

  useEffect(() => {
    // Scroll ONLY the message pane — scrollIntoView also scrolls the document
    // on iOS, visibly shifting the whole screen when the chat opens.
    const el = endRef.current?.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending.length, vv.height]);

  if (gone) {
    return (
      <div className="screen center" style={{ paddingTop: 60 }}>
        <h1>Conversation ended</h1>
        <p className="muted">This connection is no longer available.</p>
        <Link to="/matches" className="btn" style={{ display: "block", textDecoration: "none", textAlign: "center" }}>
          Back to matches
        </Link>
      </div>
    );
  }
  if (!match || !uid) return <div className="splash"><div className="spinner" /></div>;

  const remaining = Math.max(0, match.allotmentPerUser - (match.used[uid] ?? 0));
  const theirRemaining = otherUid ? Math.max(0, match.allotmentPerUser - (match.used[otherUid] ?? 0)) : 0;
  // Counters read against the CURRENT round — the last pack you both bought
  // (or the starting allotment), not the lifetime total. A paywall only fires
  // when both sides are fully spent, so `remaining` never exceeds the round.
  const roundSize = match.paywall?.resolvedTier
    ? TIER_SIZE[match.paywall.resolvedTier]
    : match.allotmentPerUser;
  const awaiting = match.state === "awaitingPayment";
  const myIntent = awaiting && match.paywall ? match.paywall.intents[uid] : null;
  const loserFirstBlocked =
    !!match.firstMessageBy && match.lastMessageAt == null && match.firstMessageBy !== uid;

  /** Load the other person's full card and open the profile view. */
  async function openPeerProfile() {
    if (!otherUid) return;
    try {
      const snap = await getDoc(doc(db, "users", otherUid));
      const d = snap.data() ?? {};
      setPeerProfile({
        uid: otherUid,
        displayName: (d.displayName as string) ?? other.name,
        age: (d.age as number) ?? 0,
        headline: (d.headline as string) ?? "",
        bio: (d.bio as string) ?? "",
        prompts: (d.prompts as { prompt: string; answer: string }[]) ?? [],
        photoURLs: (d.photoURLs as string[]) ?? [],
        locationName: (d.locationName as string) ?? "",
        distanceMiles: 0,
        boosted: false,
        compatibility: null,
        responsiveness: null,
      });
    } catch (e) {
      setError(friendly(e));
    }
  }

  async function send() {
    const text = draft.trim();
    if (!text || sending || !matchId) return;
    // Optimistic: show the bubble and clear the composer immediately — the
    // callable can cold-start, and the lag felt like the app hanging.
    setSending(true);
    setError(null);
    setDraft("");
    setPending((p) => [...p, text]);
    try {
      await sendMessage({ matchId, text });
    } catch (e) {
      setPending((p) => p.filter((t) => t !== text));
      setDraft(text); // give their words back
      setError(friendly(e));
    } finally {
      setSending(false);
    }
  }

  async function choose(intent: Tier | "declined") {
    if (!matchId || paywallBusy) return;
    setPaywallBusy(intent);
    setError(null);
    try {
      const res = await submitPaywallIntent({ matchId, intent });
      if (res.status === "expired") navigate("/matches");
    } catch (e) {
      setError(friendly(e));
    } finally {
      setPaywallBusy(null);
    }
  }

  async function useCredit() {
    if (!matchId || paywallBusy) return;
    setPaywallBusy("credit");
    setError(null);
    try {
      await applyPaywallCredit({ matchId });
      // Match doc listener flips state to "active" on unlock — no local update.
    } catch (e) {
      setError(friendly(e));
    } finally {
      setPaywallBusy(null);
    }
  }

  async function payWithCard() {
    if (!matchId || paywallBusy) return;
    setPaywallBusy("stripe");
    setError(null);
    try {
      const { url } = await createStripeCheckout({ matchId });
      // Off to Stripe Checkout; success/cancel URLs return to this thread and
      // the webhook + match listener take it from there.
      window.location.assign(url);
    } catch (e) {
      setError(friendly(e));
      setPaywallBusy(null);
    }
  }

  return (
    <div
      className={"chat-screen" + (vv.height ? " kb-open" : "")}
      style={vv.height ? { height: vv.height, transform: `translateY(${vv.top ?? 0}px)` } : undefined}
    >
      <header className="chat-header">
        <Link to="/matches" className="chat-back" aria-label="Back">‹</Link>
        {/* Tap their name/photo to view their full profile (iOS parity). */}
        <button className="chat-peer" onClick={() => void openPeerProfile()} aria-label={`View ${other.name}'s profile`}>
          {other.photo ? <img className="chat-avatar" src={other.photo} alt="" /> : <div className="chat-avatar match-avatar-empty">☺</div>}
          <div className="chat-title">
            <div className="chat-name">{other.name}</div>
            <div className="chat-sub">{awaiting ? "paused" : `${remaining} of ${roundSize} messages left`}</div>
          </div>
        </button>
        <button className="chat-menu" aria-label="More" onClick={() => setMenuOpen(true)}>⋯</button>
      </header>

      <div className="chat-scroll">
        {messages.length === 0 && !awaiting && (
          <div className="stub" style={{ paddingTop: 60 }}>
            {loserFirstBlocked
              ? <p>{other.name} gets to send the first message — hang tight.</p>
              : <p>You matched! You each have {match.allotmentPerUser} messages. Say something worth replying to.</p>}
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={"bubble-row" + (m.senderId === uid ? " mine" : "")}>
            <div className="bubble">{m.text}</div>
          </div>
        ))}
        {pending.map((t, i) => (
          <div key={`pending-${i}`} className="bubble-row mine" style={{ opacity: 0.6 }}>
            <div className="bubble">{t}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* Status strip lives ABOVE the composer — anything after it renders into
          the home-indicator zone and gets clipped. */}
      {error && <div className="error center" style={{ padding: "0 14px 8px", marginTop: 0 }}>{error}</div>}

      {/* Break the ice (fresh thread): tap a starter to prefill the composer. */}
      {!awaiting && !loserFirstBlocked && messages.length === 0 && remaining > 0 && (
        <div className="starter-strip">
          <span className="starter-label">Break the ice</span>
          <div className="starter-row">
            {starters.map((s) => (
              <button key={s} className="starter-chip" onClick={() => setDraft(s)}>{s}</button>
            ))}
          </div>
        </div>
      )}

      {/* Message meter (iOS MessageMeterView): segments + big remaining count,
          green ≥50%, orange 20–50%, red <20%. */}
      {!awaiting && (
        <div className="meter-row">
          <div className="meter-track">
            {Array.from({ length: Math.min(roundSize, 25) }, (_, i) => {
              const segs = Math.min(roundSize, 25);
              const usedSegs = Math.round(((roundSize - remaining) / roundSize) * segs);
              const ratio = remaining / roundSize;
              const color = ratio >= 0.5 ? "#30d158" : ratio >= 0.2 ? "#ff9f0a" : "#ff453a";
              return <span key={i} className="meter-seg" style={{ background: i < usedSegs ? "rgba(255,255,255,0.2)" : color }} />;
            })}
          </div>
          <b className="meter-num">{remaining}</b>
          <span className="muted" style={{ fontSize: 12 }}>left · they have {theirRemaining}</span>
        </div>
      )}

      {awaiting && match.paywall ? (
        <div className="paywall-box">
          <h2 style={{ margin: "0 0 4px" }}>You've both used your messages ⚡</h2>
          {match.paywall.deadline && (
            <p className="muted" style={{ margin: "0 0 10px" }}>
              Decide within <Countdown coarse endsAt={match.paywall.deadline.toMillis()} /> or the thread fades away.
            </p>
          )}
          {myIntent === null ? (
            <>
              {(["pack25", "pack100"] as Tier[]).map((t) => (
                <button key={t} className="btn" disabled={!!paywallBusy} onClick={() => void choose(t)}>
                  {TIER_LABEL[t]} · {TIER_PRICE[t]}
                </button>
              ))}
              <button className="btn btn-ghost" disabled={!!paywallBusy} onClick={() => void choose("declined")}>
                Let it fade
              </button>
              <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                If you both opt in, the smaller pack wins and you're only charged that.
                Pay by card right here — any credits you've banked apply automatically.
              </p>
            </>
          ) : myIntent === "declined" ? (
            <p className="muted">You chose to let this fade.</p>
          ) : match.paywall.resolvedTier ? (
            match.paywall.paid?.[uid] ? (
              <p className="muted">
                You're paid up ({TIER_LABEL[match.paywall.resolvedTier]}). Waiting on {other.name} to
                finish theirs…
              </p>
            ) : (
              <>
                <p className="muted" style={{ margin: "0 0 10px" }}>
                  You're both in — {TIER_LABEL[match.paywall.resolvedTier]}. Pay once and the
                  thread reopens the moment you both have.
                </p>
                <button className="btn" disabled={!!paywallBusy} onClick={() => void payWithCard()}>
                  {paywallBusy === "stripe" ? "Opening checkout…" : `Pay with card · ${TIER_PRICE[match.paywall.resolvedTier]}`}
                </button>
                <button className="btn btn-ghost" disabled={!!paywallBusy} onClick={() => void useCredit()}>
                  Use a banked credit
                </button>
              </>
            )
          ) : (
            <p className="muted">
              {`You're in (${TIER_LABEL[myIntent as Tier]}). Waiting on ${other.name}…`}
            </p>
          )}
        </div>
      ) : (
        <div className="chat-compose">
          {loserFirstBlocked ? (
            <div className="muted center" style={{ flex: 1, padding: 10 }}>
              {other.name} sends the first message in this one.
            </div>
          ) : (
            <div className="compose-pill">
              <textarea
                value={draft}
                maxLength={2000}
                enterKeyHint="send"
                placeholder={remaining > 0 ? "Message…" : "You're out of messages"}
                /* not `sending` — disabling a focused textarea dismisses the iOS keyboard */
                disabled={remaining === 0}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
                }}
                rows={1}
              />
              <button className="send-btn" aria-label="Send" disabled={!draft.trim() || remaining === 0 || sending} onMouseDown={(e) => e.preventDefault()} onClick={() => void send()}>
                ➤
              </button>
            </div>
          )}
        </div>
      )}
      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title={other.name}>
        <button className="btn btn-secondary" onClick={() => { setMenuOpen(false); setConfirmUnmatch(true); }}>
          Unmatch
        </button>
        <button className="btn btn-danger" onClick={() => { setMenuOpen(false); setReporting(true); }}>
          Block or report
        </button>
        <button className="btn btn-ghost" onClick={() => setMenuOpen(false)}>Cancel</button>
      </Sheet>

      <Sheet open={confirmUnmatch} onClose={() => setConfirmUnmatch(false)} title={`Unmatch ${other.name}?`}>
        <p className="muted">This removes the conversation for both of you. It can't be undone.</p>
        <button
          className="btn btn-danger"
          disabled={unmatching}
          onClick={async () => {
            if (unmatching) return;
            setUnmatching(true);
            try {
              if (matchId) await unmatch({ matchId });
              navigate("/matches");
            } catch (e) {
              setError(friendly(e));
              setConfirmUnmatch(false);
              setUnmatching(false);
            }
          }}
        >
          {unmatching ? "Unmatching…" : "Unmatch"}
        </button>
        <button className="btn btn-ghost" disabled={unmatching} onClick={() => setConfirmUnmatch(false)}>Keep chatting</button>
      </Sheet>

      {peerProfile && (
        <ProfileDetail profile={peerProfile} isPreview onClose={() => setPeerProfile(null)} />
      )}

      {otherUid && (
        <ReportSheet
          targetUid={otherUid}
          targetName={other.name}
          open={reporting}
          onClose={() => setReporting(false)}
          onDone={() => navigate("/matches")}
        />
      )}
    </div>
  );
}
